import * as React from "react";
import {inject, observer} from "mobx-react";
import type {AppState} from "../../../../../../../../data/state/AppState";
import {Field} from "../../../../../../../widgets/form/field/Field";
import {SelectInput} from "../../../../../../../widgets/input/select/SelectInput";
import {ReactUtils} from "../../../../../../../utils/ReactUtils";
import {IconButton} from "../../../../../../../widgets/button/IconButton";
import {ConfirmWindow} from "../../../../../popups/ConfirmWindow";
import {StringUtils} from "../../../../../../../../utils/data/string/StringUtils";
import {TimeUtils} from "../../../../../../../../utils/TimeUtils";
import type {TransformObj} from "../../../../../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../../../../../utils/dom/DomUtils";
import {FocusLoss} from "../../../../../../../../utils/ui/focus/FocusLoss";
import {DomPortal} from "../../../../../portal/DomPortal";
import {AppUtils} from "../../../../../../../../utils/AppUtils";
import {MathUtils} from "../../../../../../../../utils/math/MathUtils";
import type {DistanceUnitName} from "../../../../../../space/spaceeditor/logic3d/Constants";
import {SingleLineInput} from "../../../../../../../widgets/input/clicktoedit/datatypes/singleline/SingleLineInput";
import type {App} from "../../../../../../../../App";
import type {Xyicon3D} from "../../../../../../space/spaceeditor/logic3d/elements3d/Xyicon3D";
import type {BoundarySpaceMap3D} from "../../../../../../space/spaceeditor/logic3d/elements3d/BoundarySpaceMap3D";
import type {IModel} from "../../../../../../../../data/models/Model";
import {Permission} from "../../../../../../../../generated/api/base";
import {KeyboardListener} from "../../../../../../../../utils/interaction/key/KeyboardListener";
import type {PropertyName, spaceItemForProperties} from "./IMassInput";

interface IMassPropertyInputProps {
	readonly key: string;
	readonly propertyName: string;
	readonly propertyPartName: PropertyName;
	readonly propertyRef: string;
	readonly propertyLabel: string;
	readonly items: spaceItemForProperties[];
	readonly open: boolean;
	readonly distanceUnitName: DistanceUnitName;
	readonly disabled?: boolean;
	readonly noFixedPosition?: boolean;
	readonly app?: App;
	readonly appState?: AppState;
	readonly onOpen: (type: string) => void;
	readonly getSpaceItemValue: (partName: string, partRef: string, spaceItem3D: Xyicon3D | BoundarySpaceMap3D) => number;
}

interface IMassFieldInputState {
	selectedOptionId: string;
	inputValue: string;
	isUpdating: boolean;
	transform: TransformObj;
	open: boolean;
}

interface IOption {
	id: "all" | "blank" | string;
	label: string | number;
	value?: number;
}

@inject("app")
@inject("appState")
@observer
export class MassPropertyInput extends React.Component<IMassPropertyInputProps, IMassFieldInputState> {
	private _element = React.createRef<HTMLDivElement>();
	private _floating = React.createRef<HTMLDivElement>();

	constructor(props: IMassPropertyInputProps) {
		super(props);
		this.state = {
			selectedOptionId: "all",
			inputValue: "",
			isUpdating: false,
			transform: null,
			open: this.props.open,
		};
	}

	private onOpenClick = async () => {
		const {onOpen, propertyPartName} = this.props;

		onOpen(propertyPartName);
		await TimeUtils.wait(100);
		this.addListeners();
	};

	private onApply = async () => {
		const {items, propertyPartName, propertyRef, appState} = this.props;
		const {selectedOptionId, inputValue} = this.state;
		const newValue = parseFloat(inputValue) * this.correctionMultiplier;
		const itemsWithPermission = items.filter((item) => appState.actions.someSpaceItemsHaveGivenPermission([item], Permission.Update));

		KeyboardListener.getInstance().signals.up.remove(this.pressButton);

		const itemsToUpdate = itemsWithPermission.filter((item: Xyicon3D) => {
			const value = item.modelData[propertyRef as keyof IModel];

			if (selectedOptionId === "all") {
				return value !== newValue;
			} else if (selectedOptionId === "blank") {
				return StringUtils.isBlank(value);
			} else {
				return item.modelData.refId === selectedOptionId && value !== newValue;
			}
		});

		const confirmed = await ConfirmWindow.open(`${itemsToUpdate.length} of ${items.length} items will be updated. Continue?`, "Confirm Update");

		if (confirmed) {
			this.props.onOpen("");
			this.removeListeners();
			if (itemsToUpdate.length > 0) {
				this.setState({isUpdating: true});

				await this.props.appState.actions.updateProperties(
					parseFloat(inputValue),
					propertyPartName as PropertyName,
					itemsToUpdate,
					this.props.distanceUnitName,
				);
			}
			this.setState({
				isUpdating: false,
				inputValue: "",
			});
		} else {
			KeyboardListener.getInstance().signals.up.add(this.pressButton);
		}
	};

	private onBlur = (event?: MouseEvent) => {
		if (!(event?.target instanceof Element) || !this.props.app.modalContainer.contains(event?.target)) {
			this.onCancel();
		} else {
			FocusLoss.listen(this._floating.current, this.onBlur);
		}
	};

	private onCancel = () => {
		this.removeListeners();
		this.props.onOpen("");
	};

	private onOptionChange = (option: IOption) => {
		this.setState({
			selectedOptionId: option.id,
		});
	};

	private onChange = (value: string) => {
		this.setState({
			inputValue: value,
		});
	};

	private removeListeners = () => {
		AppUtils.disableScrolling(false);
		FocusLoss.stopListen(this._floating.current, this.onBlur);
		KeyboardListener.getInstance().signals.up.remove(this.pressButton);
	};

	private addListeners = () => {
		FocusLoss.listen(this._floating.current, this.onBlur);
		KeyboardListener.getInstance().signals.up.add(this.pressButton);
		AppUtils.disableScrolling(true);
	};

	private pressButton = (event: KeyboardEvent) => {
		switch (event.key) {
			case KeyboardListener.KEY_ENTER:
				this.onApply();
				break;
			case KeyboardListener.KEY_ESCAPE:
				this.onCancel();
				break;
		}
	};

	private getEditInput() {
		const {inputValue, isUpdating} = this.state;

		return (
			<SingleLineInput
				value={inputValue}
				onChange={this.onChange}
				onInput={this.onChange}
				isUpdating={isUpdating}
				onFocusLossForceBlur={true}
				disabled={this.props.disabled}
				inputType="number"
			/>
		);
	}

	private get correctionMultiplier() {
		return this.props.app.spaceViewRenderer.correctionMultiplier.current;
	}

	static getDerivedStateFromProps(props: IMassPropertyInputProps, state: IMassFieldInputState) {
		if (props.open !== state.open) {
			return {open: props.open};
		}

		return null;
	}

	public override componentDidUpdate(prevProps: IMassPropertyInputProps, prevState: IMassFieldInputState) {
		if (!this.props.noFixedPosition && !prevState.open && this.state.open && this._element.current && this._floating.current) {
			this.setState({
				transform: DomUtils.getFixedFloatingElementPosition(
					this._element.current,
					this._floating.current,
					VerticalAlignment.bottom,
					HorizontalAlignment.left,
					10,
				),
			});
		}
	}

	public override componentWillUnmount() {
		this.removeListeners();
	}

	public override render() {
		const {open, items, noFixedPosition, propertyName, propertyLabel, propertyRef, appState, getSpaceItemValue, propertyPartName, disabled} =
			this.props;
		const {selectedOptionId, isUpdating, transform} = this.state;
		const element = this._element;
		const {actions} = appState;

		const options: IOption[] = [
			{
				id: "all",
				label: "(All Values)",
			},
		];

		let allValuesMatch = true;
		let blankAdded = false;

		let first = true;
		let firstValue: number;

		for (const item of items as Xyicon3D[]) {
			const spaceItemValue = getSpaceItemValue(propertyName, propertyRef, item);
			const value = actions.formatPropValue(spaceItemValue, propertyPartName, item, this.props.distanceUnitName);

			if (!MathUtils.isValidNumber(value)) {
				continue;
			}

			// Note: if this is changed to getValue, then firstValue and option.label needs to be formatted to string
			// to avoid crashes from React.
			if (first) {
				firstValue = value;
				first = false;
			}
			if (value !== firstValue) {
				allValuesMatch = false;
			}

			if (StringUtils.isBlank(value)) {
				if (!blankAdded) {
					options.push({
						id: "blank",
						label: "(Blank Values)",
					});
					blankAdded = true;
				}
			} else {
				const id = item.modelData.refId;

				if (!options.some((option) => option.value === value)) {
					options.push({
						id: id,
						label: value,
						value: value,
					});
				}
			}
		}

		let inlineStyle: React.CSSProperties = element && {
			transform: transform?.translate,
			position: noFixedPosition ? "absolute" : "fixed",
		};

		if (noFixedPosition && inlineStyle) {
			inlineStyle.transform = "";
		}

		return (
			<div className={ReactUtils.cls("MassFieldInput", {open})}>
				<Field
					key={this.props.key}
					label={propertyLabel || propertyPartName}
					noWrap={true}
				>
					<div
						className={ReactUtils.cls("unfocused", {disabled})}
						onClick={this.onOpenClick}
						ref={this._element}
					>
						{allValuesMatch ? firstValue : <i>Multiple values</i>}
						{isUpdating && <span className="spinner" />}
					</div>
					{open && (
						<DomPortal
							destination={this.props.app.modalContainer}
							noPortal={noFixedPosition}
						>
							<div
								className={ReactUtils.cls("MassFieldInput__editArea properties", {[VerticalAlignment[transform?.vertical]]: transform})}
								style={inlineStyle}
								ref={this._floating}
							>
								<div className="header hbox alignCenter">
									<h4 className="flex_1">
										Editing multiple Values for <span>{`${propertyLabel || propertyPartName}`}</span>
									</h4>
									<IconButton
										icon="close"
										onClick={this.onCancel}
									/>
								</div>
								<div className="container">
									<Field label="Value(s) to Update">
										<SelectInput
											options={options}
											render={(option) => option.label}
											selected={options.find((option) => option.id === selectedOptionId)}
											onChange={this.onOptionChange}
											onFocusLossForceBlur={true}
										/>
									</Field>
									<Field label="New value">{this.getEditInput()}</Field>
									<div className="editButtons">
										<IconButton
											className="apply"
											icon="apply"
											title="Apply"
											onClick={this.onApply}
										/>
										<IconButton
											className="cancel"
											icon="cancel"
											title="Cancel"
											onClick={this.onCancel}
										/>
									</div>
								</div>
							</div>
						</DomPortal>
					)}
				</Field>
			</div>
		);
	}
}
